<script setup lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import type { PropType } from 'vue'
import { NButton, NCheckbox, NPopover, NTooltip } from 'naive-ui'
import { ArrowDown, ArrowUp, Settings, GripVertical, LockKeyhole } from 'lucide-vue-next'
import type { TableColumnOption, TableColumnPreference } from './useTableColumns'

const props = defineProps<{
  options: TableColumnOption[]
  preference: TableColumnPreference
  scopeKey: string
}>()
const emit = defineEmits<{ apply: [preference: TableColumnPreference]; reset: [] }>()
const show = ref(false)
const hidden = ref<string[]>([])
const order = ref<string[]>([])
const dragging = ref<string | null>(null)
const dropTarget = ref<string | null>(null)
const ColumnTitle = defineComponent({
  props: { option: { type: Object as PropType<TableColumnOption>, required: true } },
  setup: (labelProps) => () => labelProps.option.renderTitle(),
})

/** 打开面板时复制当前配置；修改草稿不触发表格重排或存储写入。 */
function openPanel(value: boolean) {
  show.value = value
  dragging.value = null
  dropTarget.value = null
  if (!value) return
  hidden.value = [...props.preference.hidden]
  const editable = props.options.filter((item) => !item.locked)
  if (editable.length && editable.every((item) => hidden.value.includes(item.id))) {
    hidden.value = hidden.value.filter((id) => id !== editable[0].id)
  }
  const rank = new Map(props.preference.order.map((id, index) => [id, index]))
  order.value = props.options
    .map((item) => item.id)
    .sort((a, b) => {
      const left = props.options.find((item) => item.id === a)!
      const right = props.options.find((item) => item.id === b)!
      return left.group === right.group && !left.locked && !right.locked
        ? (rank.get(a) ?? 1000 + props.options.indexOf(left)) -
            (rank.get(b) ?? 1000 + props.options.indexOf(right))
        : props.options.indexOf(left) - props.options.indexOf(right)
    })
}
watch(
  () => props.scopeKey,
  () => openPanel(false),
)
// 仅在浮层打开时监听 Esc，兼容焦点仍停留在工具栏入口的情况。
watch(show, (visible, _, onCleanup) => {
  if (!visible) return
  /** 处理键盘事件 event，关闭当前浮层并避免连带关闭外层业务弹窗。 */
  function closeOnEscape(event: KeyboardEvent) {
    if (event.key !== 'Escape') return
    event.preventDefault()
    event.stopPropagation()
    openPanel(false)
  }
  window.addEventListener('keydown', closeOnEscape, true)
  onCleanup(() => window.removeEventListener('keydown', closeOnEscape, true))
})
watch(
  () => props.options,
  () => {
    if (show.value) openPanel(true)
  },
)

const items = computed(() =>
  order.value
    .map((id) => props.options.find((item) => item.id === id))
    .filter((item): item is TableColumnOption => Boolean(item)),
)
const visibleCount = computed(
  () => items.value.filter((item) => !item.locked && !hidden.value.includes(item.id)).length,
)

/** 切换列 option 的勾选 checked，至少保留一个可配置的数据列。 */
function toggle(option: TableColumnOption, checked: boolean) {
  if (option.locked || (!checked && visibleCount.value <= 1)) return
  hidden.value = checked
    ? hidden.value.filter((id) => id !== option.id)
    : [...hidden.value, option.id]
}

/** 判断 source 列是否允许移动到 target 列所在位置。 */
function canMove(source: TableColumnOption | undefined, target: TableColumnOption | undefined) {
  return Boolean(
    source && target && !source.locked && !target.locked && source.group === target.group,
  )
}

/** 移动 sourceId 列到 targetId 位置，固定列及跨组拖动不参与重排。 */
function move(sourceId: string, targetId: string) {
  const source = items.value.find((item) => item.id === sourceId)
  const target = items.value.find((item) => item.id === targetId)
  if (!canMove(source, target)) return
  const next = [...order.value]
  const from = next.indexOf(sourceId)
  const to = next.indexOf(targetId)
  next.splice(from, 1)
  next.splice(to, 0, sourceId)
  order.value = next
}

/** 开始拖动列 option，将标识写入原生拖动事件 event。 */
function startDrag(event: DragEvent, option: TableColumnOption) {
  if (option.locked || !event.dataTransfer) return
  dragging.value = option.id
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData('text/plain', option.id)
}

/** 仅在可接收拖动的列 option 上允许事件 event 放置。 */
function dragOver(event: DragEvent, option: TableColumnOption) {
  if (
    !canMove(
      items.value.find((item) => item.id === dragging.value),
      option,
    )
  )
    return
  event.preventDefault()
  dropTarget.value = option.id
}

/** 将正在拖动的列放到 targetId 位置。 */
function drop(targetId: string) {
  if (dragging.value) move(dragging.value, targetId)
  endDrag()
}

/** 结束拖动并清理插入位置提示。 */
function endDrag() {
  dragging.value = null
  dropTarget.value = null
}

/** 提交一次草稿并关闭面板。 */
function apply() {
  emit('apply', { version: 1, hidden: hidden.value, order: order.value })
  openPanel(false)
}

/** 恢复默认配置并关闭面板。 */
function reset() {
  emit('reset')
  openPanel(false)
}
</script>

<template>
  <div class="column-settings-anchor">
    <NPopover
      :show="show"
      trigger="click"
      placement="bottom-end"
      :show-arrow="false"
      :style="{ padding: '0' }"
      @update:show="openPanel"
    >
      <template #trigger>
        <NTooltip
          trigger="hover"
          :disabled="show"
        >
          <template #trigger>
            <NButton
              class="column-settings-trigger"
              quaternary
              size="small"
              aria-label="列设置"
              :aria-expanded="show"
            >
              <Settings :size="12" />
            </NButton>
          </template>
          列设置
        </NTooltip>
      </template>
      <div
        class="column-settings"
        role="dialog"
        aria-label="列设置"
      >
        <div class="column-settings__title">
          列设置
        </div>
        <div class="column-settings__list">
          <div
            v-for="(item, index) in items"
            :key="item.id"
            class="column-settings__item"
            :class="{
              'column-settings__item--group': item.heading,
              'column-settings__item--drop': dropTarget === item.id,
            }"
            :style="{ paddingLeft: `${12 + item.depth * 12}px` }"
            @dragover="dragOver($event, item)"
            @drop.prevent="drop(item.id)"
          >
            <template v-if="item.heading">
              <ColumnTitle :option="item" />
            </template>
            <template v-else>
              <span
                class="column-settings__handle"
                :draggable="!item.locked"
                :title="item.locked ? '固定列' : '拖动排序'"
                @dragstart="startDrag($event, item)"
                @dragend="endDrag"
              >
                <LockKeyhole
                  v-if="item.locked"
                  :size="14"
                /><GripVertical
                  v-else
                  :size="16"
                />
              </span>
              <NCheckbox
                class="column-settings__label"
                :checked="item.locked || !hidden.includes(item.id)"
                :disabled="item.locked || (!hidden.includes(item.id) && visibleCount <= 1)"
                :aria-disabled="item.locked || (!hidden.includes(item.id) && visibleCount <= 1)"
                @update:checked="toggle(item, $event)"
              >
                <ColumnTitle :option="item" />
              </NCheckbox>
              <NButton
                quaternary
                size="tiny"
                :disabled="!canMove(item, items[index - 1])"
                aria-label="上移列"
                title="上移"
                @click="move(item.id, items[index - 1].id)"
              >
                <ArrowUp :size="14" />
              </NButton>
              <NButton
                quaternary
                size="tiny"
                :disabled="!canMove(item, items[index + 1])"
                aria-label="下移列"
                title="下移"
                @click="move(item.id, items[index + 1].id)"
              >
                <ArrowDown :size="14" />
              </NButton>
            </template>
          </div>
        </div>
        <div class="column-settings__footer">
          <NButton
            text
            size="small"
            @click="reset"
          >
            恢复默认
          </NButton>
          <span class="column-settings__spacer" />
          <NButton
            size="small"
            @click="openPanel(false)"
          >
            取消
          </NButton>
          <NButton
            type="primary"
            size="small"
            @click="apply"
          >
            应用
          </NButton>
        </div>
      </div>
    </NPopover>
  </div>
</template>

<style scoped>
.column-settings-anchor {
  position: relative;
  z-index: 5;
  height: 0;
  min-width: 0;
  flex: none;
  align-self: stretch;
  pointer-events: none;
}

/* 入口与表格滚动内容分离，右端表头为按钮留出空间。 */
.column-settings-anchor
  + :deep(.n-data-table .n-data-table-thead > tr:first-child > th:last-child) {
  padding-right: 44px;
}

.column-settings-trigger {
  position: absolute;
  top: 8px;
  right: 6px;
  width: 24px;
  height: 24px;
  padding: 0;
  pointer-events: auto;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 2px 6px rgb(15 23 42 / 8%);
}

.column-settings-trigger :deep(svg) {
  flex-shrink: 0;
}
.column-settings {
  width: 340px;
  max-width: calc(100vw - 32px);
  color: var(--color-text-primary);
}
.column-settings__title {
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid var(--color-border);
}
.column-settings__list {
  max-height: min(420px, calc(100vh - 200px));
  overflow-y: auto;
  overscroll-behavior: contain;
}
.column-settings__item {
  display: flex;
  min-height: 36px;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  font-size: 13px;
}
.column-settings__item--group {
  background: var(--color-bg-page);
  font-size: 12px;
  font-weight: 600;
}
.column-settings__item--drop {
  box-shadow: inset 0 2px var(--color-primary);
}
.column-settings__handle {
  display: flex;
  width: 16px;
  flex: none;
  color: var(--color-text-secondary);
}
.column-settings__handle[draggable='true'] {
  cursor: grab;
}
.column-settings__label {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}
.column-settings__footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid var(--color-border);
}
.column-settings__spacer {
  flex: 1;
}
</style>
